<script setup lang="ts">
  import { computed, onBeforeUnmount, ref } from 'vue';
  import { storeToRefs } from 'pinia';
  import { CustomCheckbox } from '@/shared/ui';
  import { useFormBuilderStore } from '../../model/form-builder.store';
  import { useFormPermissionsStore } from '../../model/form-permissions.store';
  import { useFormPermissionsSelectionStore } from '../../model/form-permissions-selection.store';
  import { useCanvasHeaderOffset } from '../../composables/useCanvasHeaderOffset';
  import FormBuilderStageTitle from './FormBuilderStageTitle.vue';
  import FormCanvas from './FormCanvas.vue';
  import LockedRevisionNotice from './LockedRevisionNotice.vue';

  interface Props {
    orientation: 'portrait' | 'landscape';
    zoom: number;
    locked?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), { locked: false });

  const emit = defineEmits<{
    'update:zoom': [value: number];
    'update:pageCount': [value: number];
    'update:currentPage': [value: number];
  }>();

  const formBuilderStore = useFormBuilderStore();
  const formPermissionsStore = useFormPermissionsStore();
  const selectionStore = useFormPermissionsSelectionStore();
  const { addMode, selectAllActive } = storeToRefs(selectionStore);

  const handleSelectAllChange = (event: Event) => {
    const target = event.target as HTMLInputElement;

    if (!addMode.value) {
      target.checked = false;
      return;
    }

    if (target.checked) {
      selectionStore.selectAll([...formBuilderStore.elements]);
      return;
    }

    selectionStore.clearDraft();
  };

  selectionStore.restore(formPermissionsStore.getSelectionStorageKey(), [
    ...formBuilderStore.elements,
  ]);

  onBeforeUnmount(() => selectionStore.leaveStage());

  const canvasContainerRef = ref<HTMLElement | null>(null);
  const { headerStyle } = useCanvasHeaderOffset(
    canvasContainerRef,
    computed(() => props.orientation),
    computed(() => props.zoom)
  );
</script>

<template>
  <section
    class="flex min-w-0 flex-1 flex-col overflow-hidden bg-gray-12"
    aria-label="Permissões"
    data-testid="permissions-stage"
  >
    <div class="flex flex-col gap-4 py-4">
      <div class="flex flex-col gap-4" :style="headerStyle">
        <FormBuilderStageTitle title="Permissões" />
        <LockedRevisionNotice v-if="props.locked" />

        <label class="flex w-fit items-center gap-3 text-sm leading-4 text-gray-07">
          <CustomCheckbox
            id="form-builder-permissions-select-all"
            :checked="selectAllActive"
            :disabled="props.locked || !addMode"
            @change="handleSelectAllChange"
          />
          <span class="leading-none font-medium">Selecionar tudo</span>
        </label>
      </div>
    </div>

    <div ref="canvasContainerRef" class="min-h-0 flex-1 overflow-hidden">
      <div class="flex h-full min-h-[480px] min-w-0 flex-col">
        <FormCanvas
          :orientation="props.orientation"
          :zoom="props.zoom"
          :locked="props.locked"
          interaction-mode="permissions"
          @update:zoom="emit('update:zoom', $event)"
          @update:pageCount="emit('update:pageCount', $event)"
          @update:currentPage="emit('update:currentPage', $event)"
        />
      </div>
    </div>
  </section>
</template>
